import {
	DefaultUserLicenseModel,
	DefaultUserLicenseSaveModel,
	UserLicense,
	getDefaultLicense,
	setDefaultLicense
} from "client/Api/FeaturePackages/featurePackageManagement";
import {
	Dialog,
	Form,
	FormItem,
	RadioButtonGroup,
	RadioButtonGroupItem,
	Toolbar,
	ValidationError
} from "client/Components";
import {
	AlertManager,
	useAlerts,
	useStableImperativeCallback,
	useUnmountedCancellation
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import { useChangeUserLicenseConfirmationDialog } from "./ChangeUserLicenseConfirmationDialog";
import { isNotNil } from "client/Misc/utils";
import { getUserLicenseName } from "client/Features/FeaturePackages/misc";
import { createValidationConfiguration } from "client/Validation";
import { isNotNullNorUndefined } from "client/Validation/rules";
import { IFormHandler, useFormHandler } from "client/Forms";

export type DefaultUserLicenseDialogProps = {
	onClose: () => void;
};

export const DefaultUserLicenseDialog = (props: DefaultUserLicenseDialogProps): ReactElement => {
	const {
		onClose: onCloseFromProps
	} = props;

	const [formData, setFormData] = useState<DefaultUserLicenseSaveModel>(DEFAULT_FORM_DATA);
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const onClose: () => void = useStableImperativeCallback(onCloseFromProps);

	const formHandler: IFormHandler<DefaultUserLicenseSaveModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const [confirmationDialog, openConfirmationDialog] = useChangeUserLicenseConfirmationDialog({
		title: localize("_AreYouSureYouWantToChangeWhichPackageIsAssignedByDefault"),
		trackingId: "assign-default-package-confirmation-dialog"
	});

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					const model: DefaultUserLicenseModel = await getDefaultLicense(signal);
					setFormData({ defaultLicense: model.defaultLicense });
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error();
					onClose();
				}

				setIsBusy(false);
			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, onClose]
	);

	const submitHandler = async (): Promise<void> => {
		if (isBusy) {
			return;
		}

		setIsBusy(true);

		try {
			if (!await openConfirmationDialog()) {
				setIsBusy(false);
				return;
			}

			await setDefaultLicense(formData, globalCancellation);

			alertManager.showSuccess(localize("_ChangesSaved"));
			onClose();
		} catch (e) {
			if (isCancelledError(e)) {
				return;
			}

			alertManager.showError(localize("_ChangesNotSaved"));
			console.error(e);
			setIsBusy(false);
		}
	};

	if (isNotNil(confirmationDialog)) {
		return confirmationDialog;
	}

	return (
		<Dialog
			hasBusyIndicator={isBusy}
			trackingId="default-package-dialog"
		>
			<Dialog.Header
				title={localize("_ManageDefaultPackage")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem
						label={localize("_SelectDefaultPackageForNewUsers")}
						isForGroup
					>
						<RadioButtonGroup
							{...formHandler.for("defaultLicense").attachToRadioButtonGroup()}
							options={packageOptions}
						/>
						<ValidationError {...formHandler.for("defaultLicense").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Save")}
						onClick={submitHandler}
						trackingId="save"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
DefaultUserLicenseDialog.displayName = "DefaultUserLicenseDialog";

const DEFAULT_FORM_DATA: DefaultUserLicenseSaveModel = {
	defaultLicense: UserLicense.Simplify
};

const packageOptions: readonly RadioButtonGroupItem<UserLicense>[] = [
	UserLicense.Simplify,
	UserLicense.Connect
]
	.map((license: UserLicense): RadioButtonGroupItem<UserLicense> => ({
		value: license,
		text: getUserLicenseName(license)
	}));

const validationConfiguration = createValidationConfiguration<DefaultUserLicenseSaveModel>();

validationConfiguration.forScalar("defaultLicense")
	.add(
		isNotNullNorUndefined()
	);

export function useDefaultUserLicenseDialog(): [
	element: ReactElement | null,
	open: () => void
] {
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const openHandler = useCallback(
		(): void => setIsOpen(true),
		[]
	);

	const closeHandler = useCallback(
		(): void => setIsOpen(false),
		[]
	);

	const renderElement = (): ReactElement | null => {
		if (!isOpen) {
			return null;
		}

		return (
			<DefaultUserLicenseDialog
				onClose={closeHandler}
			/>
		);
	};

	return [
		renderElement(),
		openHandler
	];
}